/**
 * Secrets phase 1 (decision 0141; direction 0140 §Secrets 1–6). Keys an agent's run may use: stored encrypted by the
 * Workspace DO (`secret-box`), write-only after entry, delivered to a run as environment variables or files. Every
 * shape here is metadata: a value never appears in a response, an event, a job or a log.
 *
 * - `workspace`: the team's; reaches the projects it lists, or all of them. Workspace owners and admins add them.
 * - `project`: one project's; any member of that project adds and edits them.
 * - `personal`: one person's; reaches only their own work, unless they share it with the team (then like a workspace
 *   secret: the projects it lists, or all of them), the way subscriptions are shared (0128).
 */
import { Schema } from "effect"
import { Millis, ProjectId, UserId } from "./ids"

/** An environment-variable-like name: upper case letters, digits and `_`, starting with a letter, ≤ 64 chars. */
export const SecretName = Schema.String.check(Schema.isPattern(/^[A-Z][A-Z0-9_]{1,63}$/))
export type SecretName = typeof SecretName.Type

/** Names a secret may not take: the runner's own variables and the CLIs' login and endpoint switches. */
export const RESERVED_SECRET_NAMES: ReadonlySet<string> = new Set([
  "PATH",
  "HOME",
  "USER",
  "SHELL",
  "PWD",
  "TMPDIR",
  "LANG",
  "TERM",
  "NODE_OPTIONS",
  "LD_PRELOAD",
  "ANTHROPIC_API_KEY",
  "ANTHROPIC_AUTH_TOKEN",
  "ANTHROPIC_BASE_URL",
  "CLAUDE_CODE_OAUTH_TOKEN",
  "CLAUDE_CONFIG_DIR",
  "CODEX_HOME",
  "OPENAI_API_KEY",
  "OPENAI_BASE_URL",
  "GH_TOKEN",
  "GITHUB_TOKEN",
  "GH_CONFIG_DIR",
  "GIT_CONFIG_GLOBAL",
])
/** Prefixes a secret name may not use (Minga's own variables, the CLIs' switches). */
export const RESERVED_SECRET_PREFIXES: ReadonlyArray<string> = ["MINGA_", "CLAUDE_CODE_", "CODEX_", "GIT_"]

export const reservedSecretName = (name: string): boolean =>
  RESERVED_SECRET_NAMES.has(name) || RESERVED_SECRET_PREFIXES.some((p) => name.startsWith(p))

export const SecretScope = Schema.Literals(["workspace", "project", "personal"])
export type SecretScope = typeof SecretScope.Type

/** `value`: the variable holds the value. `file`: the value is written to a 0600 file; the variable holds its path. */
export const SecretKind = Schema.Literals(["value", "file"])
export type SecretKind = typeof SecretKind.Type

/** Where a workspace (or shared personal) secret reaches: every project, or the projects listed. */
export const SecretReach = Schema.Union([Schema.Literal("all"), Schema.Array(ProjectId)])
export type SecretReach = typeof SecretReach.Type

/** A file secret's file name: no path, 1–128 characters. */
export const SecretFilename = Schema.Trim.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(128),
  Schema.isPattern(/^[A-Za-z0-9][A-Za-z0-9._-]*$/),
)

/** Values up to 64 KiB (a `.p8` key is ~250 bytes; a service-account JSON a few KiB). */
export const SECRET_VALUE_MAX_BYTES = 64 * 1024
export const SecretValue = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(SECRET_VALUE_MAX_BYTES),
)
export const SecretDescription = Schema.Trim.check(Schema.isMaxLength(500))

/** One secret as people and agents see it: never its value. */
export const SecretView = Schema.Struct({
  id: Schema.String,
  name: SecretName,
  description: Schema.String,
  kind: SecretKind,
  /** A file secret's file name; null for a value. */
  filename: Schema.NullOr(Schema.String),
  scope: SecretScope,
  /** The project of a `project` secret; null otherwise. */
  projectId: Schema.NullOr(ProjectId),
  /** Who it belongs to: a personal secret's person; for the others, who added it. */
  ownerId: UserId,
  /** A workspace or shared personal secret's reach; a project secret reaches its project (`[projectId]`). */
  projects: SecretReach,
  /** A personal secret its owner shared with the team. Always false for the other scopes. */
  shared: Schema.Boolean,
  createdBy: UserId,
  createdAt: Millis,
  updatedBy: UserId,
  updatedAt: Millis,
  /** When its value was last replaced; null = never rotated. */
  rotatedAt: Schema.NullOr(Millis),
  /** The last run it was delivered to (ref-less: the run id and its project), and when. */
  lastUsedAt: Schema.NullOr(Millis),
  lastUsedProjectId: Schema.NullOr(ProjectId),
})
export type SecretView = typeof SecretView.Type

/** `GET …/secrets` (workspace) and `GET …/projects/:projectId/secrets`: what the viewer may see, plus what they may do. */
export const SecretsList = Schema.Struct({
  secrets: Schema.Array(SecretView),
  /** May the viewer add workspace secrets (an owner or admin)? */
  canAddWorkspace: Schema.Boolean,
})
export type SecretsList = typeof SecretsList.Type

/** `POST …/secrets`: add one. A file secret needs `filename`; `projectId` only (and always) for a project secret. */
export const CreateSecretBody = Schema.Struct({
  name: SecretName,
  description: Schema.optionalKey(SecretDescription),
  kind: SecretKind,
  filename: Schema.optionalKey(SecretFilename),
  scope: SecretScope,
  projectId: Schema.optionalKey(ProjectId),
  projects: Schema.optionalKey(SecretReach),
  shared: Schema.optionalKey(Schema.Boolean),
  value: SecretValue,
})
export type CreateSecretBody = typeof CreateSecretBody.Type

/** `PATCH …/secrets/:id`: description, reach and (personal only) sharing. */
export const UpdateSecretBody = Schema.Struct({
  description: Schema.optionalKey(SecretDescription),
  projects: Schema.optionalKey(SecretReach),
  shared: Schema.optionalKey(Schema.Boolean),
  filename: Schema.optionalKey(SecretFilename),
})
export type UpdateSecretBody = typeof UpdateSecretBody.Type

/** `PUT …/secrets/:id/value`: rotate. The next run gets the new value; a running one keeps the old. */
export const RotateSecretBody = Schema.Struct({ value: SecretValue })
export type RotateSecretBody = typeof RotateSecretBody.Type

/** A secret's metadata the hub and the context packs use: no value, no audit fields. */
export const SecretReachRow = Schema.Struct({
  name: SecretName,
  description: Schema.String,
  kind: SecretKind,
  filename: Schema.NullOr(Schema.String),
  scope: SecretScope,
  projectId: Schema.NullOr(ProjectId),
  ownerId: UserId,
  projects: SecretReach,
  shared: Schema.Boolean,
})
export type SecretReachRow = typeof SecretReachRow.Type

/**
 * What a run receives (cloud → Platform task sandbox, or → a local runner with the feature `secrets`): environment
 * variables and files. A file's variable holds its path; the file is written 0600 before the CLI starts.
 */
export const SecretBundle = Schema.Struct({
  env: Schema.Record(Schema.String, Schema.String),
  files: Schema.Array(
    Schema.Struct({
      name: SecretName,
      filename: Schema.String,
      /** The value, base64 (a file may hold bytes a JSON string would mangle). */
      contentBase64: Schema.String,
    }),
  ),
})
export type SecretBundle = typeof SecretBundle.Type

/** Where a Platform task's file secrets are written (`/run/minga` is the node user's private config dir, never backed up). */
export const PLATFORM_SECRET_DIR = "/run/minga/secrets"
